import { osnovaApi } from '../base';
import type { AdminAgentStat, AdminAuditEntry, AdminStats, AdminSystemStats, AdminTimeseriesPoint, AdminUser } from '../types';

export const adminApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    // ─────────── Admin ───────────
    adminListUsers: b.query<
      { items: AdminUser[] },
      {
        active?: boolean;
        role?: string;
        search?: string;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (p) => ({ url: '/admin/users', params: p ?? undefined }),
      providesTags: ['AdminUsers'],
    }),
    adminCreateUser: b.mutation<
      AdminUser,
      {
        email: string;
        password: string;
        full_name: string;
        department?: string;
        /** Список ролей: ["lawyer", "dispatcher_operator", ...]. Можно несколько. */
        roles: string[];
      }
    >({
      query: (body) => ({ url: '/admin/users', method: 'POST', body }),
      invalidatesTags: ['AdminUsers', 'AdminStats'],
    }),
    adminUpdateUser: b.mutation<
      AdminUser,
      {
        id: string;
        email?: string;
        full_name?: string;
        department?: string;
        /** VK Teams userId; пустая строка — отвязать. */
        vk_user_id?: string;
        /** Полная замена ролей пользователя. */
        roles?: string[];
        is_active?: boolean;
        password?: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/users/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['AdminUsers'],
    }),
    /** Hard delete — полностью удаляет пользователя из БД. */
    adminDeleteUser: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/admin/users/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AdminUsers', 'AdminStats'],
    }),
    /** Recovery: админ сбрасывает 2FA юзеру (потерял телефон). */
    adminDisable2fa: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/admin/users/${id}/disable-2fa`,
        method: 'POST',
      }),
      invalidatesTags: ['AdminUsers'],
    }),
    adminStats: b.query<AdminStats, void>({
      query: () => '/admin/stats',
      providesTags: ['AdminStats'],
    }),
    adminTimeseries: b.query<{ items: AdminTimeseriesPoint[] }, { days?: number } | void>({
      query: (p) => ({
        url: '/admin/stats/timeseries',
        params: p ?? undefined,
      }),
      providesTags: ['AdminStats'],
    }),
    adminTopAgents: b.query<{ items: AdminAgentStat[] }, void>({
      query: () => '/admin/stats/top-agents',
      providesTags: ['AdminStats'],
    }),
    adminSystem: b.query<AdminSystemStats, void>({
      query: () => '/admin/system',
    }),
    adminAudit: b.query<{ items: AdminAuditEntry[] }, { limit?: number } | void>({
      query: (p) => ({ url: '/admin/audit', params: p ?? undefined }),
      providesTags: ['AdminAudit'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useAdminListUsersQuery,
  useAdminCreateUserMutation,
  useAdminUpdateUserMutation,
  useAdminDeleteUserMutation,
  useAdminDisable2faMutation,
  useAdminStatsQuery,
  useAdminTimeseriesQuery,
  useAdminTopAgentsQuery,
  useAdminSystemQuery,
  useAdminAuditQuery,
} = adminApi;
